/**
 * WuaPlusSuccess — post-payment landing at
 * `/wua-plus/success?session_id=...`. Polls /wua-plus/status until
 * `payment_status === "paid"`, then triggers /wua-plus/generate, then
 * navigates to /wua-plus/report/{report_id}.
 *
 * Poll cadence is 2s to stay well under Stripe's webhook delivery
 * skew in preview envs. We bail after 90s and offer a manual retry
 * button so buyers never sit staring at a spinner forever.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 90_000;

export default function WuaPlusSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const timer = useRef(null);

  const [status, setStatus] = useState("polling");        // polling | generating | done | timeout | error
  const [attempts, setAttempts] = useState(0);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); setErrMsg("Missing session_id"); return; }
    let cancelled = false;
    const started = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/wua-plus/status/${encodeURIComponent(sessionId)}`);
        if (data.payment_status === "paid") {
          setStatus("generating");
          // Generate the report — safe to call multiple times, endpoint
          // is idempotent by session_id.
          try {
            const { data: rpt } = await api.post("/wua-plus/generate", {
              session_id: sessionId,
            });
            if (cancelled) return;
            setStatus("done");
            nav(`/wua-plus/report/${rpt.report_id}`, { replace: true });
          } catch (e) {
            if (cancelled) return;
            setStatus("error");
            setErrMsg(e?.response?.data?.detail || "Report generation failed.");
          }
          return;
        }
        // Still pending — schedule next tick unless we've timed out.
        setAttempts((a) => a + 1);
        if (Date.now() - started >= POLL_TIMEOUT_MS) {
          setStatus("timeout");
          return;
        }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setErrMsg(e?.response?.data?.detail || "Could not confirm the payment.");
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [sessionId, nav]);

  return (
    <div
      data-testid="wua-plus-success-page"
      className="max-w-2xl mx-auto px-6 lg:px-10 py-16 text-center"
    >
      {status === "polling" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#4C1D95]" />
          <h1 className="mt-6 font-editorial text-3xl text-[#002147]">
            Confirming your payment…
          </h1>
          <p className="mt-3 text-stone-600 text-[14px] leading-relaxed">
            Stripe usually acknowledges instantly, but in rare cases can
            take a few seconds. Keep this tab open — we'll redirect you
            the moment it's confirmed.
          </p>
          <div className="mt-4 font-mono text-[11px] text-stone-400">
            Attempt {attempts + 1}
          </div>
        </>
      )}
      {status === "generating" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-emerald-700" />
          <h1 className="mt-6 font-editorial text-3xl text-[#002147]">
            Writing your report…
          </h1>
          <p className="mt-3 text-stone-600 text-[14px] leading-relaxed">
            Payment confirmed. Our editorial LLM is now composing a
            personalised strategy report from your profile and shortlist.
            This usually takes 20-40 seconds.
          </p>
        </>
      )}
      {status === "timeout" && (
        <>
          <AlertTriangle className="h-10 w-10 mx-auto text-amber-600" />
          <h1 className="mt-6 font-editorial text-3xl text-[#002147]">
            Still confirming your payment
          </h1>
          <p className="mt-3 text-stone-600 text-[14px] leading-relaxed">
            Stripe hasn't confirmed the transaction yet. If your card was
            charged, we'll email you the report link within a few minutes.
            You can also re-run this check now.
          </p>
          <Button
            data-testid="wua-plus-success-retry"
            className="mt-6 bg-[#4C1D95] hover:bg-[#3B0764] text-white"
            onClick={() => window.location.reload()}
          >
            Re-check
          </Button>
        </>
      )}
      {status === "error" && (
        <>
          <AlertTriangle className="h-10 w-10 mx-auto text-red-600" />
          <h1 className="mt-6 font-editorial text-3xl text-[#002147]">
            Something went wrong
          </h1>
          <p className="mt-3 text-stone-600 text-[14px] leading-relaxed">
            {errMsg || "An unexpected error happened while finalising your report."}
          </p>
          <Link
            to="/plus"
            data-testid="wua-plus-success-back"
            className="mt-6 inline-block text-[#4C1D95] underline"
          >
            Back to WUA+
          </Link>
        </>
      )}
      {status === "done" && (
        <>
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-700" />
          <h1 className="mt-6 font-editorial text-3xl text-[#002147]">Ready.</h1>
          <p className="mt-3 text-stone-600 text-[14px]">
            Redirecting to your report…
          </p>
        </>
      )}
    </div>
  );
}
